// frontend/src/hooks/useLivestock.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications, type NotificationData } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { livestockApi } from '@/api/livestock';
import type {
  LivestockCreate,
  LivestockUpdate,
  LivestockStatusUpdate,
} from '@/types/livestock';

export function useLivestockList(params?: { status?: string; search?: string }) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['livestock', selectedTank?.id, params?.status, params?.search],
    queryFn: () => livestockApi.list(selectedTank!.id, params),
    enabled: !!selectedTank?.id,
  });
}

export function useLivestockDetail(livestockId: number | null) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['livestock-detail', selectedTank?.id, livestockId],
    queryFn: () => livestockApi.get(selectedTank!.id, livestockId!),
    enabled: !!selectedTank?.id && !!livestockId,
  });
}

export function useLivestockSummary() {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['livestock-summary', selectedTank?.id],
    queryFn: () => livestockApi.getSummary(selectedTank!.id),
    enabled: !!selectedTank?.id,
  });
}

export interface CreateLivestockOptions {
  /** Where its notices show; the app's default (bottom right) when not given. */
  notificationPosition?: NotificationData['position'];
  /** false: no "added" notice (the caller shows the new organism); failures still show. */
  successNotice?: boolean;
}

export function useCreateLivestock(options: CreateLivestockOptions = {}) {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();
  const position = options.notificationPosition;
  const successNotice = options.successNotice ?? true;

  return useMutation({
    mutationFn: (data: LivestockCreate) =>
      livestockApi.create(selectedTank!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['livestock', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['livestock-summary', selectedTank?.id] });
      if (!successNotice) return;
      notifications.show({
        title: 'Success',
        message: 'Livestock added successfully',
        color: 'green',
        position,
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add livestock',
        color: 'red',
        position,
      });
    },
  });
}

export function useUpdateLivestock() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LivestockUpdate }) =>
      livestockApi.update(selectedTank!.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['livestock', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Livestock updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update livestock',
        color: 'red',
      });
    },
  });
}

export function useUpdateLivestockStatus() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LivestockStatusUpdate }) =>
      livestockApi.updateStatus(selectedTank!.id, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['livestock', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['livestock-summary', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['livestock-detail'] });
      // Status updates now create timeline entries server-side, so refresh the timeline
      queryClient.invalidateQueries({ queryKey: ['timeline'] });
      notifications.show({
        title: 'Status Updated',
        message: `Livestock marked as ${variables.data.status}`,
        color: variables.data.status === 'deceased' ? 'red' : 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update status',
        color: 'red',
      });
    },
  });
}

export function useDeleteLivestock() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => livestockApi.delete(selectedTank!.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['livestock', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['livestock-summary', selectedTank?.id] });
      notifications.show({
        title: 'Deleted',
        message: 'Livestock removed successfully',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete livestock',
        color: 'red',
      });
    },
  });
}
